import { startWixConnect } from '../utils/wixConnect';
import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { FormControl, InputLabel, Select, MenuItem, Button, FormControlLabel, Switch } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClone, faCircleCheck, faTimesCircle, faCalendarCheck, faArrowDown, faHeadset, faRotate, faCircleInfo, faPlug, faExternalLink, faSpinner, faComment, faWindowMaximize, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { useTranslation, Trans } from 'react-i18next';
import '../translations.js';
import './integration.css';
import WebflowSitePanel from '../components/WebflowSitePanel';
import { startWebflowConnect } from '../utils/webflowConnect';
import FirstBotPrompt from '../components/FirstBotPrompt.js';
import ChannelPreview from '../components/ChannelPreview.js';
import { guardWrite, reconcileSelectedBotId } from '../utils/permissions';

// When rendered inside the Channels-page modal, `only` pins the component
// to a single tab (no tab bar, no bot dropdown — it uses the globally
// selected bot from localStorage).
const ONLY_TAB_INDEX = { snippet: 0, wix: 1, webflow: 2, wordpress: 3, sharelink: 4 };

function Integration({ addBot, only }) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [bots, setBots] = useState([]);
  const [selectedTab, setSelectedTab] = useState(
    only != null
      ? (ONLY_TAB_INDEX[only] ?? 0)
      : location.state?.tab === "webflow" ? 2 : 0
  );
  const [selectedBotId, setSelectedBotId] = useState(localStorage.getItem("selectedBotId") || '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [webflowStatus, setWebflowStatus] = useState(null);
  // From webflow/status: live | pending | off_in_page | off, and the site row
  // (the site the agent is on, or was last turned off on) with its address.
  const [webflowState, setWebflowState] = useState(null);
  const [webflowSiteRow, setWebflowSiteRow] = useState(null);
  // Sites our script came off at Disconnect (to publish in the Designer).
  const [webflowDisconnected, setWebflowDisconnected] = useState(null);

  const [wixStatus, setWixStatus] = useState(null);
  const [wixProduct, setWixProduct] = useState(null);
  const [wixUrl, setWixUrl] = useState(null);
  const [wixSiteName, setWixSiteName] = useState(null);
  // 'own_code': the script is on, but the connected site is built with its own code and never shows it (backend
  // wix_site_check.py); the owner pastes the website code instead
  const [wixSiteCheck, setWixSiteCheck] = useState(null);
  const [wixSnippetCopied, setWixSnippetCopied] = useState(false);
  const [site, setSite] = useState(null);
  const [sites, setSites] = useState([]);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [webflowError, setWebflowError] = useState(null);
  const [copiedId, setCopiedId] = useState(false);
  const [snippetMode, setSnippetMode] = useState('widget');
  // Suppress the chip's "un-draw" border animation until the user actually
  // switches tabs — otherwise the inactive tab flashes a backwards animation
  // on first open. The active tab still draws.
  const [snippetTouched, setSnippetTouched] = useState(false);

  const fetchBots = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_BASE_URL}bots?user_id=${localStorage.getItem("user_id") || ''}&include_knowledge=False`;

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const data = await response.json();
      const completedBots = data
        .filter((bot) => bot.status === "completed")
        .sort((a, b) => a.name.localeCompare(b.name));
      setBots(completedBots);

      // Reconcile the selected bot against this workspace's bots so
      // /wix/status, /webflow/status etc. don't 403 on a stale id
      // (previous workspace, deleted bot, or invalid `?bot_id=` URL).
      setSelectedBotId(reconcileSelectedBotId(completedBots));
    } catch (err) {
      console.error('Error fetching bots:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBots();
  }, [fetchBots]);

  // Check for bot_id in URL params and pre-select it. Only honor the
  // deep-link if it actually belongs to this workspace's bots (once
  // they're loaded) — otherwise the request would 403 and the page
  // would render with a broken selection.
  useEffect(() => {
    const botIdFromUrl = searchParams.get('bot_id');
    if (!botIdFromUrl) return;
    if (bots.length === 0) return; // wait for fetchBots
    const exists = bots.some((b) => String(b.id) === String(botIdFromUrl));
    if (!exists) return; // ignore stale/foreign deep-link
    setSelectedBotId(botIdFromUrl);
    localStorage.setItem("selectedBotId", botIdFromUrl);
  }, [searchParams, bots]);

  const fetchWebflowStatus = useCallback(async () => {
    if (!selectedBotId) return;
    setIntegrationLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/status?bot_id=${selectedBotId}`
      );
      const data = await res.json();
      setWebflowStatus(data.status);
      setWebflowState(data.state || null);
      const row = data.site || data.last_site || null;
      setWebflowSiteRow(row);

      if (data.sites?.length) {
        setSites(data.sites);
        const pick = (row && data.sites.find((s) => s.id === row.id)) || row || data.sites[0];
        setSite({ id: pick.id, name: pick.name });
      } else {
        setSites([]);
        setSite(null);
      }
    } catch (err) {
      console.error('Failed to fetch Webflow status:', err);
      setWebflowStatus('error');
    } finally {
      setIntegrationLoading(false);
    }
  }, [selectedBotId]);

  const fetchWixStatus = useCallback(async () => {
    if (!selectedBotId) return;
    setIntegrationLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/status?bot_id=${selectedBotId}`
      );
      const data = await res.json();
      if (!res.ok) throw new Error('Could not check Wix');
      setWixStatus(data.status);
      setWixProduct(data.wix_product || null);
      setWixUrl(data.url);
      setWixSiteName(data.site_name);
      setWixSiteCheck(data.site_check || null);
    } catch (err) {
      console.error('Failed to fetch Wix status:', err);
      setWixStatus('error');
    } finally {
      setIntegrationLoading(false);
    }
  }, [selectedBotId]);

  // Check if selected bot is bound to a Wix instance and auto-switch to Wix tab
  useEffect(() => {
    if (!only && selectedBotId && bots.length > 0) {
      const selectedBot = bots.find(bot => bot.id === selectedBotId);
      if (selectedBot && (selectedBot.wix_instance_id || selectedBot.wix_product)) {
        setSelectedTab(1);
      }
    }
  }, [selectedBotId, bots, only]);

  useEffect(() => {
    if (selectedTab === 1 && selectedBotId) fetchWixStatus();
  }, [selectedTab, selectedBotId, fetchWixStatus]);

  useEffect(() => {
    if (selectedTab === 2 && selectedBotId) fetchWebflowStatus();
  }, [selectedTab, selectedBotId, fetchWebflowStatus]);

  const handleBotChange = (e) => {
    const botId = e.target.value;
    setSelectedBotId(botId);
    localStorage.setItem("selectedBotId", botId);
    // Reset status when bot changes
    setWebflowStatus(null);
    setWebflowState(null);
    setWebflowSiteRow(null);
    setWixStatus(null);
    setWixProduct(null);
    setWixUrl(null);
    setWixSiteName(null);
    setWixSiteCheck(null);
    setSite(null);
    setSites([]);
  };

  const widgetSnippet = selectedBotId
    ? `<div id="chat-widget-container" data-user-id="${selectedBotId}"></div>
<script src="https://robert-kloepsch.github.io/ultimo-bots-widget/dist/bundle.js" defer></script>
<a href="https://www.ultimo-bots.com" target="_blank" rel="noopener" style="display: none;">Our website uses an intelligent AI assistant powered by Ultimo Bots to improve customer service.</a>`
    : '';

  const iframeSnippet = selectedBotId
    ? `<iframe\n  src="https://chat.ultimo-bots.com/${selectedBotId}"\n  style="width: 100%; height: 100%; border: none;"\n  allow="microphone"\n></iframe>`
    : '';

  const integrationSnippet = snippetMode === 'widget' ? widgetSnippet : iframeSnippet;
  // An agent on a Webflow site runs through the Ultimo Bots app: no snippet to
  // paste (Webflow: code goes through the Custom Code API, where the App can
  // version and remove it).
  const webflowHostedAgent = !!bots.find((b) => b.id === selectedBotId)?.webflow_hosted;

  const shareableLink = selectedBotId ? `https://chat.ultimo-bots.com/${selectedBotId}` : '';

  const handleCopyToClipboard = () => {
    if (!selectedBotId) return;
    navigator.clipboard
      .writeText(integrationSnippet)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      })
      .catch((e) => console.error('Failed to copy snippet:', e));
  };

  const handleCopyWixSnippet = () => {
    if (!widgetSnippet) return;
    navigator.clipboard
      .writeText(widgetSnippet)
      .then(() => {
        setWixSnippetCopied(true);
        setTimeout(() => setWixSnippetCopied(false), 2000);
      })
      .catch((e) => console.error('Failed to copy snippet:', e));
  };
  const wixLive = wixStatus === 'token_with_code';
  const wixNotOnSite = wixLive && wixSiteCheck === 'own_code';

  const handleCopyShareLink = () => {
    if (!shareableLink) return;
    navigator.clipboard
      .writeText(shareableLink)
      .then(() => {
        setShareLinkCopied(true);
        setTimeout(() => setShareLinkCopied(false), 2000);
      })
      .catch((e) => console.error('Failed to copy share link:', e));
  };

  const handleWebflowConnect = async () => {
    if (!guardWrite()) return;
    try {
      // The OAuth state is minted server-side and bound to this session.
      const res = await startWebflowConnect({ botId: selectedBotId });
      if (res?.reconnected) await fetchWebflowStatus();
    } catch (err) {
      console.error('Webflow connect failed:', err);
      setWebflowError(t('integration_webflow_connect_failed'));
    }
  };

  const handleActivateWebflowChatbot = async () => {
    if (!selectedBotId || !site) return;
    if (!guardWrite()) return;
    try {
      setIntegrationLoading(true);
      setWebflowError(null);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/activate_chatbot?bot_id=${selectedBotId}&site_id=${site.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 429) {
        const data = await res.json();
        setWebflowError(data.message || 'Webflow rate limit reached. Please try again in 1 minute.');
      } else if (res.ok) {
        await fetchWebflowStatus();
      } else {
        const data = await res.json().catch(() => ({}));
        setWebflowError(data.status === 'token_revoked' ? t('wf_token_revoked') : t('wf_action_failed'));
        if (data.status === 'token_revoked') await fetchWebflowStatus();
      }
    } catch (err) {
      console.error('Integration failed:', err);
      setWebflowError(t('wf_action_failed'));
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleDeactivateWebflowChatbot = async () => {
    if (!selectedBotId || !site) return;
    if (!guardWrite()) return;
    try {
      setIntegrationLoading(true);
      setWebflowError(null);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/deactivate_chatbot?bot_id=${selectedBotId}&site_id=${site.id}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.status === 429) {
        const data = await res.json();
        setWebflowError(data.message || 'Webflow rate limit reached. Please try again in 1 minute.');
      } else if (res.ok) {
        await fetchWebflowStatus();
      } else {
        // Revoked authorization: the script cannot be removed through the
        // API any more. It no longer answers; tell the owner how to remove it.
        const data = await res.json().catch(() => ({}));
        setWebflowError(data.status === 'token_revoked' ? t('wf_token_revoked_removal') : t('wf_action_failed'));
        if (data.status === 'token_revoked') await fetchWebflowStatus();
      }
    } catch (err) {
      console.error('Failed to remove chatbot:', err);
      setWebflowError(t('wf_action_failed'));
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleRemoveWebflow = async () => {
    if (!selectedBotId) return;
    if (!guardWrite()) return;
    try {
      setIntegrationLoading(true);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}webflow/remove_webflow?bot_id=${selectedBotId}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      if (res.ok) {
        const d = await res.json().catch(() => ({}));
        setWebflowDisconnected(Array.isArray(d.removed_sites) ? d.removed_sites : []);
        await fetchWebflowStatus();
      } else {
        setWebflowError(t('wf_action_failed'));
      }
    } catch (err) {
      console.error('Failed to remove chatbot:', err);
      setWebflowError(t('wf_action_failed'));
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleActivateWixChatbot = async () => {
    if (!selectedBotId) return;
    if (!guardWrite()) return;
    try {
      setIntegrationLoading(true);
      const r = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/activate_chatbot?bot_id=${selectedBotId}`,
        { method: 'POST' }
      );
      if (r.ok) {
        await fetchWixStatus();
      } else {
        console.error('Activation failed:', await r.text());
      }
    } catch (err) {
      console.error('Failed to activate Wix chatbot:', err);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleDeactivateWixChatbot = async () => {
    if (!selectedBotId) return;
    if (!guardWrite()) return;
    try {
      setIntegrationLoading(true);
      const r = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}wix/deactivate_chatbot?bot_id=${selectedBotId}`,
        { method: 'POST' }
      );
      if (r.ok) {
        await fetchWixStatus();
      } else {
        console.error('Deactivation failed:', await r.text());
      }
    } catch (err) {
      console.error('Failed to deactivate Wix chatbot:', err);
    } finally {
      setIntegrationLoading(false);
    }
  };

  const handleCopyBotId = () => {
    if (!selectedBotId) return;
    navigator.clipboard
      .writeText(selectedBotId)
      .then(() => {
        setCopiedId(true);
        setTimeout(() => setCopiedId(false), 2000);
      })
      .catch((e) => console.error('Failed to copy Bot ID:', e));
  };

  const integrationHelpSection = only ? null : (
    <div className="integration-help-box">
      <div className="integration-options">
        <div className='integration-support-header-container'>
          <FontAwesomeIcon className="integratin-support-icon" icon={faHeadset} />
          <h3 style={{ margin: '0' }}>{t('need_help_integration')}</h3>
        </div>
        <div className="option-item" style={{ margin: '0' }}>
          <p style={{ margin: '0', marginBottom: '10px' }}>
            <Trans i18nKey="integration_help_body_html" components={{ strong: <strong /> }} />
          </p>
          <button
            className="integration-option-button"
            onClick={() => window.open('https://calendly.com/build-ultimo-bots/integration_support', '_blank')}
          >
            <FontAwesomeIcon className="integratin-button-icon" icon={faCalendarCheck} />
            {t('book_integration_call')}
          </button>
        </div>
      </div>
    </div>
  );

  const tabs = [
    {
      label: t('integration_tab_snippet'),
      content: selectedBotId ? (
        <>
          <div className={`platform-filters snippet-mode-filters${snippetTouched ? '' : ' no-undraw'}`}>
            <button
              type="button"
              className={`platform-filter ${snippetMode === 'widget' ? 'active' : ''}`}
              onClick={() => { setSnippetMode('widget'); setCopied(false); setSnippetTouched(true); }}
            >
              <FontAwesomeIcon icon={faComment} style={{ color: '#5616ea' }} />
              {t('integration_snippet_widget')}
              <svg className="platform-filter-border-svg" aria-hidden="true" focusable="false">
                <rect className="platform-filter-border-rect" pathLength="100" />
              </svg>
            </button>
            <button
              type="button"
              className={`platform-filter ${snippetMode === 'iframe' ? 'active' : ''}`}
              onClick={() => { setSnippetMode('iframe'); setCopied(false); setSnippetTouched(true); }}
            >
              <FontAwesomeIcon icon={faWindowMaximize} style={{ color: '#5616ea' }} />
              {t('integration_snippet_iframe')}
              <svg className="platform-filter-border-svg" aria-hidden="true" focusable="false">
                <rect className="platform-filter-border-rect" pathLength="100" />
              </svg>
            </button>
          </div>
          <div className="integration-tab-content">
            <div className="integration-info-box">
              <FontAwesomeIcon className="integration-info-icon" icon={faCircleInfo} />
              <div className="integration-info-text">
                {snippetMode === 'widget' ? (
                  <>
                    <p>
                      <Trans i18nKey="integration_widget_info_html" components={{ strong: <strong />, code: <code /> }} />
                    </p>
                    <ul>
                      <li>{t('integration_widget_bullet1')}</li>
                      <li>{t('integration_widget_bullet2')}</li>
                      <li>{t('integration_widget_bullet3')}</li>
                    </ul>
                  </>
                ) : (
                  <>
                    <p>
                      <Trans i18nKey="integration_iframe_info_html" components={{ strong: <strong />, code: <code /> }} />
                    </p>
                    <ul>
                      <li>{t('integration_iframe_bullet1')}</li>
                      <li>{t('integration_iframe_bullet2')}</li>
                      <li>{t('integration_iframe_bullet3')}</li>
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div className='arrow-down-container'>
              <FontAwesomeIcon icon={faArrowDown} />
            </div>
            {webflowHostedAgent ? (
              <p className="integration-connect-text" data-testid="wf-no-snippet">{t('wf_no_snippet_for_webflow')}</p>
            ) : (
            <div className="code-wrapper">
              <button className="copy-btn" onClick={handleCopyToClipboard}>
                <div className="copy-button-content">
                  <FontAwesomeIcon icon={copied ? faCircleCheck : faClone} />
                  {copied ? t('copied_exclaim') : t('copy')}
                </div>
              </button>
              <div className="code-container">
                <pre>
                  <code>{integrationSnippet}</code>
                </pre>
              </div>
            </div>
            )}
          </div>
          {integrationHelpSection}
        </>
      ) : (
        <p>{t('no_bot_selected')}</p>
      ),
    },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/wix-logo.png"
            alt={t('integration_wix_logo_alt')}
            style={{ width: '38px', height: '21px' }}
          />
        </span>
      ),
      content: selectedBotId ? (
        <>
          <div className="integration-tab-content">
            <div className="integration-info-box">
              <FontAwesomeIcon className="integration-info-icon" icon={faCircleInfo} />
              <div className="integration-info-text">
                <p>
                  <Trans i18nKey="integration_wix_info_html" components={{ strong: <strong /> }} />
                </p>
                <ul>
                  <li>{t('integration_wix_bullet1')}</li>
                  <li>{t('integration_wix_bullet2')}</li>
                </ul>
              </div>
            </div>
            {integrationLoading ? (
              <div className="integration-loading-container">
                <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
              </div>
            ) : wixStatus === 'error' ? (<div role="alert"><p>Could not check your Wix connection.</p><button onClick={fetchWixStatus}>Retry</button></div>) : wixProduct && wixProduct.status !== 'installed' ? (
              <div className="integration-info-text">
                <p>{t('connection_expired_message')}</p>
                <button className="ac-link-btn" onClick={() => { if (guardWrite()) startWixConnect(selectedBotId, {action: 'reconnect', onConnected: fetchWixStatus}); }}>{t('reconnect')}</button>
              </div>
            ) : ['no_token', 'uninstalled'].includes(wixStatus) ? (
              <>
                <p className="integration-connect-text">
                  <Trans i18nKey="integration_wix_connect_text_html" components={{ strong: <strong /> }} />
                </p>
                <Button
                  variant="contained"
                  className="integration-connect-button"
                  onClick={() => {
                    if (!guardWrite()) return;
                    startWixConnect(selectedBotId, { onConnected: fetchWixStatus });
                  }}
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                  }}
                >
                  <FontAwesomeIcon icon={faPlug} style={{ marginRight: '8px' }} />
                  {t('connect')}
                </Button>
              </>
            ) : (
              <div className="control-group control-group-no-gap">
                <div className='wix-status-container'>
                  <div>
                    <h3 className="wix-status-title">{t('integration_wix_connection_title')}</h3>
                    <p className="wix-status-line">
                      <FontAwesomeIcon icon={faCircleCheck} className="wix-icon connected" />
                      {t('integration_wix_connected_line')}&nbsp;
                      {wixUrl ? (
                        <a href={wixUrl} target="_blank" rel="noopener noreferrer" className="wix-url">{wixUrl}</a>
                      ) : (
                        <strong>{wixSiteName || t('integration_unknown_site')}</strong>
                      )}
                    </p>
                    <Button
                      variant="contained"
                      onClick={() => {
                        if (!guardWrite()) return;
                        startWixConnect(selectedBotId, { action: 'change', onConnected: fetchWixStatus });
                      }}
                      sx={{
                        p: 0.3,
                        paddingRight: 1,
                        paddingLeft: 1,
                        backgroundColor: '#5616ea',
                        '&:hover': { backgroundColor: '#4a14d6' },
                        mt: 1,
                      }}
                    >
                      <div className='change-wix-button-content'>
                        <FontAwesomeIcon icon={faRotate} className="wix-icon-change" />
                        {t('integration_change')}
                      </div>
                    </Button>
                  </div>

                  <div>
                    <h3 className="wix-status-title">{t('integration_chatbot_integration_title')}</h3>
                    <p className="wix-status-line">
                      <FontAwesomeIcon
                        icon={wixNotOnSite ? faTriangleExclamation : wixLive ? faCircleCheck : faTimesCircle}
                        className={`wix-icon ${wixNotOnSite ? 'not-visible' : wixLive ? 'live' : 'not-live'}`}
                      />
                      {t('integration_your_chatbot_is')}&nbsp;
                      <span className={`wix-status-chip ${wixNotOnSite ? 'chip-not-visible' : wixLive ? 'chip-live' : 'chip-not-live'}`}>
                        {wixNotOnSite ? t('integration_status_not_visible')
                          : wixLive ? t('integration_status_live') : t('integration_status_not_live')}
                      </span>
                    </p>
                    {wixNotOnSite && (
                      <div className="wix-own-code" data-testid="wix-own-code">
                        <p className="wix-own-code-text">{t('integration_wix_own_code_text')}</p>
                        <div className="code-wrapper">
                          <button className="copy-btn" onClick={handleCopyWixSnippet}>
                            <div className="copy-button-content">
                              <FontAwesomeIcon icon={wixSnippetCopied ? faCircleCheck : faClone} />
                              {wixSnippetCopied ? t('copied_exclaim') : t('copy')}
                            </div>
                          </button>
                          <div className="code-container">
                            <pre>
                              <code>{widgetSnippet}</code>
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <FormControlLabel
                  control={
                    <Switch
                      checked={wixStatus === 'token_with_code'}
                      onChange={wixStatus === 'token_with_code' ? handleDeactivateWixChatbot : handleActivateWixChatbot}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': { color: '#5616ea' },
                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#5616ea' },
                        '& .MuiSwitch-track': { backgroundColor: '#ccc' },
                      }}
                    />
                  }
                  label={
                    <strong>
                      {wixStatus === 'token_with_code'
                        ? t('integration_deactivate_chatbot')
                        : t('integration_make_chatbot_live')}
                    </strong>
                  }
                />
              </div>
            )}
          </div>
          {integrationHelpSection}
        </>
      ) : (
        <p>{t('no_bot_selected')}</p>
      ),
    },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/webflow-logo.png"
            alt={t('integration_webflow_logo_alt')}
            style={{ width: '85px', height: '15px' }}
          />
        </span>
      ),
      content: selectedBotId ? (
        <>
          <div className="integration-tab-content">
            <WebflowSitePanel
              botId={selectedBotId}
              status={webflowStatus === 'error' ? 'error' : webflowStatus}
              state={webflowState}
              site={webflowSiteRow}
              sites={sites}
              selectedSiteId={site?.id || ''}
              onSelectSite={(id) => { const s = sites.find((x) => x.id === id); if (s) setSite({ id: s.id, name: s.name }); }}
              busy={integrationLoading}
              error={webflowError}
              disconnectedSites={webflowDisconnected}
              onConnect={handleWebflowConnect}
              onDisconnect={handleRemoveWebflow}
              onToggle={(on) => (on ? handleActivateWebflowChatbot() : handleDeactivateWebflowChatbot())}
              onPublished={() => setTimeout(fetchWebflowStatus, 1500)}
              onRetry={fetchWebflowStatus}
            />
          </div>
          {integrationHelpSection}
        </>
      ) : (
        <p>{t('no_bot_selected')}</p>
      ),
    },
    {
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <img
            src="/wordpress-logo.png"
            alt={t('integration_wordpress_logo_alt')}
            style={{ width: '90px', height: '23px' }}
          />
        </span>
      ),
      content: selectedBotId ? (
        <>
          <div className="integration-tab-content">
            <div className="integration-info-box">
              <FontAwesomeIcon className="integration-info-icon" icon={faCircleInfo} />
              <div className="integration-info-text">
                <p>
                  <Trans i18nKey="integration_wordpress_info_html" components={{ strong: <strong /> }} />
                </p>
                <ul>
                  <li>{t('integration_wordpress_bullet1')}</li>
                  <li>{t('integration_wordpress_bullet2')}</li>
                  <li>{t('integration_wordpress_bullet3')}</li>
                </ul>
              </div>
            </div>
            <div className="wordpress-tab-c">
              <button className="wp-btn-c left" onClick={handleCopyBotId}>
                <FontAwesomeIcon icon={copiedId ? faCircleCheck : faClone} />
                <span>{copiedId ? t('integration_bot_id_copied') : t('integration_copy_bot_id')}</span>
              </button>

              <button
                className="wp-btn-c right"
                onClick={() =>
                  window.open(
                    process.env.REACT_APP_WP_PLUGIN_URL || 'https://wordpress.org/plugins/ultimo-bots/',
                    '_blank'
                  )
                }
              >
                <FontAwesomeIcon icon={faArrowDown} />
                <span>{t('integration_install_plugin')}</span>
              </button>
            </div>
          </div>
          {integrationHelpSection}
        </>
      ) : (
        <p>{t('no_bot_selected')}</p>
      ),
    },
    {
      label: (
        <span className="tab-with-badge">
          {t('share_link')}
        </span>
      ),
      content: selectedBotId ? (
        <>
          <div className="integration-tab-content">
            <div className="integration-info-box">
              <FontAwesomeIcon className="integration-info-icon" icon={faCircleInfo} />
              <div className="integration-info-text">
                <p>
                  <Trans i18nKey="integration_sharelink_info_html" components={{ strong: <strong /> }} />
                </p>
                <ul>
                  <li>{t('integration_sharelink_bullet1')}</li>
                  <li>{t('integration_sharelink_bullet2')}</li>
                  <li>{t('integration_sharelink_bullet3')}</li>
                </ul>
              </div>
            </div>

            {/* Same order as the guide modals: info → preview → instruction. */}
            <ChannelPreview variant="sharelink" botId={selectedBotId} />

            <div className="share-link-container">
              <p className="integration-connect-text">{t('integration_sharelink_connect_text')}</p>
              <div className="share-link-buttons">
                <Button
                  variant="contained"
                  className="integration-connect-button"
                  href={shareableLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: '#5616ea',
                    '&:hover': { backgroundColor: '#4a14d6' },
                  }}
                >
                  <FontAwesomeIcon icon={faExternalLink} style={{ marginRight: '8px' }} />
                  {t('integration_open_chat_window')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCopyShareLink}
                  sx={{
                    backgroundColor: 'white',
                    borderColor: '#5616ea',
                    color: '#5616ea',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      borderColor: '#4a14d6',
                    },
                  }}
                >
                  <FontAwesomeIcon icon={shareLinkCopied ? faCircleCheck : faClone} style={{ marginRight: '8px' }} />
                  {shareLinkCopied ? t('copied_exclaim') : t('integration_copy_link')}
                </Button>
              </div>
            </div>
          </div>
          {integrationHelpSection}
        </>
      ) : (
        <p>{t('no_bot_selected')}</p>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="loading-container">
        <FontAwesomeIcon icon={faSpinner} spin className="main-loading-spinner" />
      </div>
    );
  }

  if (bots.length === 0) {
    return <FirstBotPrompt onAddBot={addBot} />;
  }

  if (error) {
    // `error` is not a key in any locale — it rendered the literal string
    // "error" in every language. `error_heading` is the existing translated one.
    return <div>{t('error_heading')}: {error}</div>;
  }

  return (
    <div className="integration-page">
      {!only && (
      <>
      <div className="tabs">
        {tabs.map((tab, index) => (
          <button
            key={index}
            className={`tab ${selectedTab === index ? 'active' : ''}`}
            onClick={() => setSelectedTab(index)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="selection-container">
        <FormControl sx={{
          maxWidth: '300px',
          width: '100%',
          '& label.Mui-focused': {
            color: '#5616ea',
          },
          '& .MuiOutlinedInput-root': {
            '&.Mui-focused fieldset': {
              border: "1px solid #515151",
            },
          },
        }}>
          <InputLabel id="bot-select-label">{t('select_bot')}</InputLabel>
          <Select
            labelId="bot-select-label"
            value={selectedBotId || ''}
            onChange={handleBotChange}
            className="bot-select"
            label={t('select_bot')}
          >
            {bots.map((botOption) => (
              <MenuItem
                key={botOption.id}
                value={botOption.id}
                className="bot-select-item"
              >
                {botOption.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      </>
      )}
      <div>{tabs[selectedTab].content}</div>
    </div>
  );
}

export default Integration;

